import { SEOHead } from "@/components/seo/SEOHead.tsx";
import { useTenant } from "@/stores/tenantStore.tsx";
import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/Button.tsx";
import { apiPost } from "@/api/client.ts";

export function ResetPasswordPage() {
  const { siteName } = useTenant();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const uid = searchParams.get("uid") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token || !uid) {
    return (
      <>
        <h1 className="text-2xl font-semibold text-surface-900 mb-2">Invalid reset link</h1>
        <p className="text-[13px] text-surface-400 mb-6">This password reset link is invalid or has expired.</p>
        <Link to="/forgot-password"><Button fullWidth>Request a new link</Button></Link>
      </>
    );
  }

  if (done) {
    return (
      <>
        <h1 className="text-2xl font-semibold text-surface-900 mb-2">Password updated</h1>
        <p className="text-[13px] text-surface-400 mb-6">Your password has been reset. You can now log in.</p>
        <Link to="/login"><Button fullWidth>Log in</Button></Link>
      </>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setLoading(true);
    try {
      await apiPost("/auth/reset-password", { token, userId: uid, newPassword: password });
      setDone(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEOHead title="Reset Password" description="Set a new password for your TubeForge account." path="/reset-password" noindex={true} />
      <div className="lg:hidden mb-8 text-center">
        <span className="text-2xl font-bold text-surface-900">{siteName}</span>
      </div>
      <h1 className="text-2xl font-semibold text-surface-900 mb-1">Set new password</h1>
      <p className="text-[13px] text-surface-400 mb-6">Enter your new password below.</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-[12px] font-medium text-surface-700 mb-1.5">New password</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters" required
            className="w-full h-10 px-3 rounded-lg border border-surface-200 bg-white text-[13px] text-surface-900 placeholder:text-surface-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors" />
        </div>
        <div>
          <label className="block text-[12px] font-medium text-surface-700 mb-1.5">Confirm password</label>
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
            placeholder="••••••••" required
            className="w-full h-10 px-3 rounded-lg border border-surface-200 bg-white text-[13px] text-surface-900 placeholder:text-surface-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors" />
        </div>
        {error && <p className="text-[12px] text-danger">{error}</p>}
        <Button type="submit" loading={loading} fullWidth>Reset password</Button>
      </form>
    </>
  );
}
